/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/hold-points/useQaHoldPointSignoff.ts
 * FILE_ID: JOBHUB-QA-HOLD-SIGNOFF-HOOK-001
 * PURPOSE: Request, sign and reject one check's hold point from the Run row.
 * FLOW: Sign off -> signer name -> signature pad -> PNG uploaded to QDrive ->
 *       approve with that file id. The approve endpoint checks the signer can
 *       see the file, so the upload must come from the signer's own session.
 */
import { useState } from 'react'
import type { QaApi, QaHoldPoint, QaItemInstance } from '../../../qa/qaApi'
import { qaItemSignoff } from '../../../qa/qaHoldPoints'
import { qaErrorMessage } from '../qaTabSelectors'

export type QaSignoffMode = 'idle' | 'sign' | 'pad' | 'reject'

export function holdPointName(item: Pick<QaItemInstance, 'display_number' | 'item_number' | 'title'>): string {
  return `${item.display_number ?? item.item_number} ${item.title}`.trim().slice(0, 160)
}

export function useQaHoldPointSignoff({
  item,
  areaId,
  jobId,
  holdPoints,
  canSign,
  api,
  onMutated,
  onWriteError,
}: {
  item: QaItemInstance
  areaId: string
  jobId: number
  holdPoints: readonly QaHoldPoint[]
  canSign: boolean
  api: Pick<QaApi, 'requestHoldPoint' | 'approveHoldPoint' | 'rejectHoldPoint' | 'uploadFile'>
  onMutated: () => void
  onWriteError?: (message: string) => void
}) {
  const signoff = qaItemSignoff(item, holdPoints)
  const holdPoint = signoff.holdPoint
  const [mode, setMode] = useState<QaSignoffMode>('idle')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [signerName, setSignerName] = useState('')
  const [signerRole, setSignerRole] = useState('')
  const [comment, setComment] = useState('')

  const run = async (fn: () => Promise<unknown>, fallback: string) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      setMode('idle')
      setComment('')
      onMutated()
    } catch (err) {
      const message = qaErrorMessage(err, fallback)
      setError(message)
      onWriteError?.(message)
    } finally {
      setBusy(false)
    }
  }

  const request = () => run(
    () => api.requestHoldPoint({ areaId, itemInstanceId: item.id, name: holdPointName(item) }),
    'Could not request sign-off.',
  )

  const onPadSaved = (png: Uint8Array) => {
    const target = holdPoint
    setMode('sign')
    if (!target) return
    void run(async () => {
      const file = new File([new Uint8Array(png)], `signature-${item.id}.png`, { type: 'image/png' })
      const { id } = await api.uploadFile(file, jobId)
      await api.approveHoldPoint(target.id, {
        signatureAssetId: id,
        signerName: signerName.trim(),
        signerRole: signerRole.trim(),
      })
    }, 'Could not save the signature.')
  }

  const reject = () => {
    const target = holdPoint
    if (!target || !comment.trim()) return
    void run(() => api.rejectHoldPoint(target.id, comment.trim()), 'Could not reject the sign-off.')
  }

  return {
    signoff,
    canRequest: mode === 'idle' && (signoff.state === 'needed' || signoff.state === 'rejected'),
    canDecide: mode === 'idle' && signoff.state === 'requested' && canSign,
    mode,
    busy,
    error,
    signerName,
    signerRole,
    comment,
    setSignerName,
    setSignerRole,
    setComment,
    request: () => void request(),
    startSign: () => setMode('sign'),
    startReject: () => setMode('reject'),
    cancel: () => setMode('idle'),
    openPad: () => { if (signerName.trim()) setMode('pad') },
    closePad: () => setMode('sign'),
    onPadSaved,
    reject,
  }
}

export type QaHoldPointSignoffFlow = ReturnType<typeof useQaHoldPointSignoff>
